import { DocumentModel, DocumentSection, DocumentBlock } from '../ast/document.types';

function generateId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
}

export function addSection(
  doc: DocumentModel,
  title: string = 'New Section',
  type: DocumentSection['type'] = 'custom',
  targetIndex?: number
): DocumentModel {
  const newSection: DocumentSection = {
    id: generateId('sec'),
    title,
    type,
    enabled: true,
    collapsed: false,
    blocks: [
      {
        id: generateId('blk'),
        type: 'text',
        createdAt: new Date().toISOString(),
        data: { text: '' },
      },
    ],
  };

  const sections = [...doc.sections];
  if (typeof targetIndex === 'number' && targetIndex >= 0 && targetIndex <= sections.length) {
    sections.splice(targetIndex, 0, newSection);
  } else {
    sections.push(newSection);
  }

  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections,
  };
}

export function removeSection(doc: DocumentModel, sectionId: string): DocumentModel {
  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: doc.sections.filter((s) => s.id !== sectionId),
  };
}

export function updateSection(
  doc: DocumentModel,
  sectionId: string,
  updates: Partial<Omit<DocumentSection, 'id'>>
): DocumentModel {
  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: doc.sections.map((s) =>
      s.id === sectionId ? { ...s, ...updates } : s
    ),
  };
}

export function moveSection(
  doc: DocumentModel,
  sectionId: string,
  direction: 'up' | 'down'
): DocumentModel {
  const index = doc.sections.findIndex((s) => s.id === sectionId);
  if (index === -1) return doc;

  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= doc.sections.length) return doc;

  const newSections = [...doc.sections];
  const [removed] = newSections.splice(index, 1);
  newSections.splice(targetIndex, 0, removed);

  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: newSections,
  };
}

export function duplicateSection(doc: DocumentModel, sectionId: string): DocumentModel {
  const index = doc.sections.findIndex((s) => s.id === sectionId);
  if (index === -1) return doc;

  const original = doc.sections[index];
  const duplicatedBlocks: DocumentBlock[] = original.blocks.map((blk) => ({
    ...blk,
    id: generateId('blk'),
    createdAt: new Date().toISOString(),
  }));

  const duplicatedSection: DocumentSection = {
    ...original,
    id: generateId('sec'),
    title: `${original.title} (Copy)`,
    blocks: duplicatedBlocks,
  };

  const newSections = [...doc.sections];
  newSections.splice(index + 1, 0, duplicatedSection);

  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: newSections,
  };
}

export function toggleSectionEnabled(doc: DocumentModel, sectionId: string): DocumentModel {
  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: doc.sections.map((s) =>
      s.id === sectionId ? { ...s, enabled: !s.enabled } : s
    ),
  };
}
